import { useQuery, keepPreviousData, type UseQueryResult } from '@tanstack/react-query';
import { fetchGql } from '@/lib/graphql_client';
import {
  RelatedOrgsDocument,
  RelatedOrgsConnectionDocument,
  type OrgRelationshipKind,
  type RelationshipDirection,
  type OnboardingState,
  type RelatedOrgFieldsFragment,
  type RelatedOrgSort,
  type ClientLifecycleState,
} from '@/gql/graphql';
import { useContextStore } from '@/lib/session/context-store';
import type { OrgPersona } from './relationships.types';
import type { RelatedOrgVM } from './related-orgs.types';

// EW-FIX-77 T0248/T0250 (item D1) — `clientLead` is selected on `RelatedOrgs`/`RelatedOrgsConnection` themselves, not the shared
// `RelatedOrgFields` fragment (see `related-orgs.graphql`'s own comment on why), so it isn't part of `RelatedOrgFieldsFragment`'s
// generated type — accept it as a separate, optional intersected field instead of widening the fragment.
type RowWithClientLead = RelatedOrgFieldsFragment & { clientLead?: { clientTenantId: string; leadUserId: string } | null };

function toRelatedOrgVM(o: RowWithClientLead): RelatedOrgVM {
  return {
    tenantId: o.tenantId,
    name: o.name,
    persona: (o.persona as OrgPersona | null) ?? null,
    countryPlaceId: o.countryPlaceId ?? null,
    country: o.country ?? null,
    countryCode: o.countryCode ?? null,
    email: o.email ?? null,
    poc: o.poc
      ? {
          firstName: o.poc.firstName ?? null,
          lastName: o.poc.lastName ?? null,
          email: o.poc.email ?? null,
          contactNumber: o.poc.contactNumber ?? null,
        }
      : null,
    relationship: {
      kind: o.relationship.kind,
      direction: o.relationship.direction,
      scope: o.relationship.scope ?? [],
    },
    viaOrgs: (o.viaOrgs ?? []).map((v) => ({ tenantId: v.tenantId, name: v.name ?? null })),
    primaryPartyId: o.primaryPartyId ?? null,
    // The server's per-client legal-entity count. Was selected by the fragment but never mapped, so every row read null (EW-FIX-71 follow-up).
    entityCount: o.entityCount ?? null,
    agreementStatus: o.agreementStatus ?? null,
    agreementEndDate: o.agreementEndDate ?? null,
    agreementContractId: o.agreementContractId ?? null,
    lifecycleState: o.lifecycleState ?? null,
    clientLead: o.clientLead ?? null,
  };
}

export interface RelatedOrgsParams {
  kinds?: OrgRelationshipKind[];
  direction?: RelationshipDirection;
  /** "SA" | "AGENCY" | "CLIENT" | "VENDOR" — owner persona of the org. */
  persona?: string;
  /** Country place id (scalar, single). Prefer `countries` for multiselect. */
  country?: string;
  /** OR-set of country place ids — matches orgs in any of these countries. */
  countries?: string[];
  q?: string;
  /** ENT-267 — OR-set of managing-agency tenantIds (match rows whose viaOrgs ∩ managedBy ≠ ∅). */
  managedBy?: string[];
  /** ENT-267 — also include platform-direct rows (no non-platform manager). UNION with managedBy. */
  direct?: boolean;
  /**
   * T12 (AR-1104) — OR-set: only orgs whose derived onboarding/activation state is one of these.
   * `relatedOrgsConnection`-only (see that query's own schema doc) — `useRelatedOrgs`/`relatedOrgs`
   * does not thread this through today (the SA branch has no activation filter UI).
   */
  activationStates?: OnboardingState[];
  /**
   * T12 (AR-1104) — OR-set: only orgs whose relationship `scope[]` intersects this set.
   * `relatedOrgsConnection`-only, same reasoning as `activationStates` above.
   */
  serviceScopes?: string[];
  /**
   * EW-FIX-12b — the server ordering, `relatedOrgsConnection`-only (the generated `RelatedOrgSort`: ENTITY_COUNT_DESC | NAME_ASC | NAME_DESC).
   * A SLOT IN THE QUERY KEY: two different sorts must never share a cache entry, or a second click would show the first order. Omitted =
   * the server's own default (NAME_ASC); a page that cares sends it explicitly rather than relying on that.
   */
  sort?: RelatedOrgSort;
  /**
   * EW-FIX-08 cut-over — OR-set: only clients whose five-state lifecycle is one of these, `relatedOrgsConnection`-only. Filters SERVER-side, in the same
   * query pass as `total`, so paging is right beyond page 1. ⚠️ The server treats a list of ALL FIVE states as NO filter (an "all" affordance built on this
   * would silently become "unfiltered"); an empty list is also no filter. A SLOT IN THE QUERY KEY.
   */
  lifecycleStates?: ClientLifecycleState[];
  /**
   * Default 50. `limit > 200` rejects (400, class-validator `@Max`). `limit` 101-200 PASSES
   * validation but is then silently clamped to 100 server-side (`clampLimit`,
   * `@entite/common-utils`, range `[1,100]`) — a caller in that band gets back fewer rows
   * than requested with no error. The only safe assumption is the effective 100-row clamp,
   * not the 200 validation ceiling; do not raise this past 100 without confirming the caller
   * doesn't rely on `rows.length === limit` for a "has next page" check.
   */
  limit?: number;
  offset?: number;
  /** Caller gate (persona/visibility); ANDed with the active-context check. */
  enabled?: boolean;
}

/**
 * Optional second arg — lets the legacy re-point hooks (T02–T04) reshape rows
 * while keeping TanStack Query's referential stability. `select` runs on the
 * mapped `RelatedOrgVM[]`.
 */
export interface RelatedOrgsOptions<T = RelatedOrgVM[]> {
  select?: (rows: RelatedOrgVM[]) => T;
}

export const DEFAULT_RELATED_ORGS_LIMIT = 50;

export const relatedOrgsKey = (tenantId: string | undefined, p: RelatedOrgsParams) =>
  [
    'relationships',
    'related-orgs',
    tenantId ?? 'self',
    p.kinds?.join(',') ?? '',
    p.direction ?? '',
    p.persona ?? '',
    p.country ?? '',
    p.countries?.join(',') ?? '',
    p.q ?? '',
    p.managedBy?.join(',') ?? '',
    p.direct ? 'direct' : '',
    p.activationStates?.join(',') ?? '',
    p.serviceScopes?.join(',') ?? '',
    p.sort ?? '',
    p.lifecycleStates?.join(',') ?? '',
    p.limit ?? DEFAULT_RELATED_ORGS_LIMIT,
    p.offset ?? 0,
  ] as const;

/**
 * ENT-255 org directory — the single data layer that replaces the deprecated
 * `myManagedClients` / `assignableAgencies` / `myManagingAgencies` presets.
 *
 * Caller-scoped by the active context (call after `switchContext`). persona /
 * kinds / direction / country / q are server-side filters applied BEFORE
 * pagination; rows are sorted name ASC and carry their `relationship`, owner
 * `persona`, `country`, POC and non-platform `viaOrgs` inline (no per-row FE
 * fan-out).
 *
 * FAIL-CLOSED (AC-8): an acl read failure (`RELATED_ORGS_READ_FAILED`) or a
 * missing active context propagates as `isError` — the query never resolves to
 * an empty list on failure. Callers MUST render a load error, not an empty state.
 */
export function useRelatedOrgs<T = RelatedOrgVM[]>(
  params: RelatedOrgsParams = {},
  options: RelatedOrgsOptions<T> = {},
): UseQueryResult<T> {
  const tenantId = useContextStore((s) => s.active?.tenantId);
  const enabled = Boolean(tenantId) && (params.enabled ?? true);
  const limit = params.limit ?? DEFAULT_RELATED_ORGS_LIMIT;
  const offset = params.offset ?? 0;

  return useQuery<RelatedOrgVM[], Error, T>({
    queryKey: relatedOrgsKey(tenantId, { ...params, limit, offset }),
    enabled,
    placeholderData: keepPreviousData,
    select: options.select,
    // Do NOT swallow errors into [] — fail-closed contract (AC-8).
    queryFn: async () => {
      const { relatedOrgs } = await fetchGql(RelatedOrgsDocument, {
        kinds: params.kinds,
        direction: params.direction,
        persona: params.persona,
        country: params.country,
        countries: params.countries?.length ? params.countries : undefined,
        q: params.q || undefined,
        managedBy: params.managedBy?.length ? params.managedBy : undefined,
        direct: params.direct || undefined,
        pagination: { limit, offset },
      });
      return relatedOrgs.map(toRelatedOrgVM);
    },
  });
}

export interface RelatedOrgsConnectionResult {
  items: RelatedOrgVM[];
  /** True, page-independent total for the current filters — see `RelatedOrgConnection.total`'s
   * schema doc: computed via `COUNT(*) OVER()` in the SAME query pass as `items`, so the two
   * cannot diverge in scope. Unlike `RelatedOrgsParams.limit`, this carries no clamp ceiling. */
  total: number;
}

/**
 * T09 (clients-ux-consistency follow-up) — the total-carrying sibling of `useRelatedOrgs`, for
 * callers that need an exact portfolio count alongside the page (a "Showing X-Y of N" footer, a
 * count-driven metric tile) without a separate query. Wraps `relatedOrgsConnection`, which
 * `relatedOrgs` itself is now `@deprecated` in favour of (server-side, not yet removed) — see
 * that field's `deprecationReason` in the schema. Same `RelatedOrgsParams` shape and row mapping
 * as `useRelatedOrgs`; does NOT change `useRelatedOrgs`'s own return shape, so existing callers
 * (the SA branch) are unaffected.
 */
export function useRelatedOrgsConnection(
  params: RelatedOrgsParams = {},
): UseQueryResult<RelatedOrgsConnectionResult> {
  const tenantId = useContextStore((s) => s.active?.tenantId);
  const enabled = Boolean(tenantId) && (params.enabled ?? true);
  const limit = params.limit ?? DEFAULT_RELATED_ORGS_LIMIT;
  const offset = params.offset ?? 0;

  return useQuery<RelatedOrgsConnectionResult>({
    queryKey: [...relatedOrgsKey(tenantId, { ...params, limit, offset }), 'connection'],
    enabled,
    placeholderData: keepPreviousData,
    // Do NOT swallow errors into an empty/zero result — same fail-closed contract as
    // useRelatedOrgs (AC-8): an acl read failure must surface as a load error, not silently
    // report "0 clients".
    queryFn: async () => {
      const { relatedOrgsConnection } = await fetchGql(RelatedOrgsConnectionDocument, {
        kinds: params.kinds,
        direction: params.direction,
        persona: params.persona,
        country: params.country,
        countries: params.countries?.length ? params.countries : undefined,
        q: params.q || undefined,
        managedBy: params.managedBy?.length ? params.managedBy : undefined,
        direct: params.direct || undefined,
        activationStates: params.activationStates?.length ? params.activationStates : undefined,
        serviceScopes: params.serviceScopes?.length ? params.serviceScopes : undefined,
        sort: params.sort,
        lifecycleStates: params.lifecycleStates?.length ? params.lifecycleStates : undefined,
        pagination: { limit, offset },
      });
      return {
        items: relatedOrgsConnection.items.map(toRelatedOrgVM),
        total: relatedOrgsConnection.total,
      };
    },
  });
}
